import { Context, ExtraContext } from '@utilitywarehouse/tracking-core';

import {
  ProductSubtype,
  ProductType,
  Quote,
  SelectIpQuoteMutation,
} from '@generated/gql/graphql';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  SignupCoreServicesEntity,
  useTrackSignupIpAddedToBasketOnce,
} from '@tracking/trackingEvents';
import { getGroupCombinations } from '@tracking/utils';
import { buildOptimizelyEntity } from '@tracking/utils/buildOptimizelyEntity';
import { findTrackingContextIndex } from '@tracking/utils/findTrackingContextIndex';
import { parseValueToPennies } from '@utils/price';

const getIpMonthlyCost = (quotes: Quote[]) => {
  const ipQuote = quotes.find(
    (quote) =>
      quote.product.type === ProductType.Insurance &&
      quote.product.subtype === ProductSubtype.IncomeProtector &&
      quote.selected
  );

  return ipQuote?.totalMonthlyCost?.value || '';
};

const updateTrackingContext = (
  contexts: ExtraContext<Context>[],
  selectResult: SelectIpQuoteMutation
): ExtraContext<Context>[] => {
  const coreServiceContextIndex = findTrackingContextIndex(
    contexts,
    'signup_core_services'
  );

  // Should never happen
  if (coreServiceContextIndex === -1) {
    return contexts;
  }

  const coreServiceContext = contexts[coreServiceContextIndex]
    .data as SignupCoreServicesEntity;

  const {
    selectQuote: {
      monthlyCost: { value: totalPrice },
      quotes,
    },
  } = selectResult;

  if (!coreServiceContext.insurance) {
    contexts[coreServiceContextIndex].data.insurance = true;
    contexts[coreServiceContextIndex].data.number_of_core_services++;
  }

  const ipPrice = getIpMonthlyCost(quotes as Quote[]);

  if (ipPrice) {
    contexts[coreServiceContextIndex].data.insurance_price =
      parseValueToPennies(ipPrice);
  }

  contexts[coreServiceContextIndex].data.total_price =
    parseValueToPennies(totalPrice);
  contexts[
    coreServiceContextIndex
  ].data.core_service_group_quantity_combinations =
    getGroupCombinations(coreServiceContext);

  return contexts;
};

const useTrackIpSelected = () => {
  const { trackingContexts } = useTrackingContext();
  const trackIpAddedToBasket = useTrackSignupIpAddedToBasketOnce();

  const trackIpSelected = (basketData: SelectIpQuoteMutation) => {
    const updatedTrackingContexts = updateTrackingContext(
      trackingContexts,
      basketData
    );

    trackIpAddedToBasket(
      {},
      {
        contexts: [...updatedTrackingContexts, ...buildOptimizelyEntity()],
      }
    );
  };

  return { trackIpSelected };
};

export { useTrackIpSelected };
